import { isUndefined } from 'lodash-es';

import { TrendProps } from '../../core/components/single-value-renderer/components/trend/types/trend-props.js';
import { SingleValueRenderer } from '../../core/components/single-value-renderer/SingleValueRenderer.js';
import {
  defaultXAxisConfig,
  defaultYAxisConfig,
} from '../../core/components/sparkline-display/constants.js';
import type { SparklineDisplayProps } from '../../core/components/sparkline-display/SparklineDisplay.js';
import {
  PartialSingleValueGridConfig,
  SingleValueGridAccessorsConfig,
} from '../types/single-value-grid.js';
import { buildConfig } from '../utils/build-config.js';
import { getConfigFromAccessors } from '../utils/get-config-from-accessors.js';
import { getDataFromAccessors } from '../utils/get-data-from-accessors.js';

export const SingleValueDisplay = ({
  record,
  propsConfig,
  accessors,
}: {
  record: string | number | Record<string, unknown>;
  propsConfig: PartialSingleValueGridConfig;
  accessors: SingleValueGridAccessorsConfig;
}) => {
  if (typeof record === 'string' || typeof record === 'number') {
    // in case it is a simple array of strings or numbers, it will just use the props configuration or the defaults
    const config = buildConfig(propsConfig);

    return (
      <SingleValueRenderer
        data={record}
        color={config.color}
        formatter={config.value?.formatter}
        unit={config.value?.unit}
        thresholds={config.thresholds}
        alignment={config.alignment}
        trendProps={config.trend}
        applyThresholdBackground={config.applyThresholdBackground}
      />
    );
  }

  const { label, prefixIcon, value, trendData, sparklineData } =
    getDataFromAccessors(record, accessors);

  // in case it is an array of records, it will override the config with the accessors configuration
  const accessorsConfig = getConfigFromAccessors(record, accessors);

  const config = buildConfig(propsConfig, accessorsConfig);

  let sparklineProps: SparklineDisplayProps | undefined = undefined;

  if (!isUndefined(sparklineData)) {
    sparklineProps = {
      ...config.sparkline,
      data: sparklineData,
      xAxis: defaultXAxisConfig,
      yAxis: defaultYAxisConfig,
    };
  }

  let trendProps: TrendProps | undefined = undefined;

  if (!isUndefined(config.trend) || !isUndefined(trendData)) {
    trendProps = {
      ...config.trend,
      value: trendData?.value,
      label: trendData?.label,
      direction: trendData?.direction,
    };
  }

  return (
    <SingleValueRenderer
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      data={value!}
      label={label}
      prefixIcon={prefixIcon}
      color={config.color}
      formatter={config.value?.formatter}
      unit={config.value?.unit}
      thresholds={config.thresholds}
      alignment={config.alignment}
      sparklineProps={sparklineProps}
      trendProps={trendProps}
      applyThresholdBackground={config.applyThresholdBackground}
    />
  );
};
